/**
 * "All <goal type>" grouped reporting helpers.
 *
 * Mirrors the "All <goal type>" helpers in backend_api/include/tests_lib.php. Every goal type gets an
 * "All ..." option as the LAST entry in its group. Selecting it strips the
 * goal-specific metrics from the variation rows and makes each variation
 * expandable into its own table listing every goal of that type.
 *
 * An "All" selection is the normal goal type with an "_all" suffix
 * (goal_all / button_all / postback_all / webhook_all) and carries
 * ALL_OPTION_VALUE as its dropdown value — the radio group already says which
 * goal type is in play, so one sentinel covers all four types.
 *
 * Shared by the Testing Results page and the Test History Report.
 */

/**
 * Legacy plain sentinel. The API now returns this value encrypted, exactly like a
 * goal id, in `all_option_value` — always prefer that token and treat it as opaque.
 * This constant remains the fallback for a response that predates the change.
 */
export const ALL_OPTION_VALUE = '__all__';

/**
 * Is this dropdown value the "All ..." sentinel?
 *
 * Accepts the encrypted token the API supplies and the legacy plain sentinel, which
 * mirrors is_goal_all_option() on the server so both ends agree.
 */
export function isAllOptionValue(value: any, token?: string): boolean {
  if (typeof value !== 'string' || value === '') return false;
  return value === ALL_OPTION_VALUE || (!!token && value === token);
}

const ALL_SUFFIX = '_all';

export const GOAL_TYPES = ['goal', 'button', 'postback', 'webhook'] as const;

/** Radio value -> goal type, matching the "Conversions to" radio group. */
export const RADIO_TO_GOAL_TYPE: Record<string, string> = {
  '1': 'goal',
  '2': 'button',
  '3': 'postback',
  '4': 'webhook',
};

/**
 * The radios are gone from the UI, but convRedioBtn still decides which
 * convto* field a report request carries. Keeping it in sync from the dropdown
 * means the entire request-building path stays untouched.
 */
export const GOAL_TYPE_TO_RADIO: Record<string, string> = {
  goal: '1',
  button: '2',
  postback: '3',
  webhook: '4',
};

/** 'button_all' -> 'button'. Anything else is returned untouched. */
export function baseGoalType(goalType: any): string {
  if (typeof goalType !== 'string' || goalType === '') return '';
  return goalType.endsWith(ALL_SUFFIX) ? goalType.slice(0, -ALL_SUFFIX.length) : goalType;
}

export function isAllGoalType(goalType: any): boolean {
  if (typeof goalType !== 'string' || goalType === '') return false;
  const base = baseGoalType(goalType);
  return base !== goalType && (GOAL_TYPES as readonly string[]).includes(base);
}

/** 'button' -> 'button_all'. Unknown types are returned untouched. */
export function allGoalType(goalType: any): string {
  const base = baseGoalType(goalType);
  return (GOAL_TYPES as readonly string[]).includes(base) ? base + ALL_SUFFIX : goalType;
}

/** Label for the grouped option, e.g. 'button' -> 'All Buttons'. */
export function allGoalLabel(goalType: any): string {
  const labels: Record<string, string> = {
    goal: 'All Page Goals',
    button: 'All Buttons',
    postback: 'All Postbacks',
    webhook: 'All Webhooks',
  };
  return labels[baseGoalType(goalType)] || 'All Page Goals';
}

/** Heading each category carries in the single grouped dropdown. */
export const GOAL_GROUP_LABEL: Record<string, string> = {
  goal: 'Page Goals',
  webhook: 'Webhook Goals',
  postback: 'Postback Goals',
  button: 'Button Goals',
};

/** The order the client asked for: page, webhook, postback, button. */
export const GOAL_GROUP_ORDER = ['goal', 'webhook', 'postback', 'button'] as const;

/** Which list keys each goal type uses in the conversions-dropdown response. */
export const GOAL_LIST_KEYS: Record<string, { id: string; name: string }> = {
  goal:     { id: 'goal_id',     name: 'goal_name' },
  webhook:  { id: 'webhook_id',  name: 'webhook_name' },
  postback: { id: 'postback_id', name: 'postback_name' },
  button:   { id: 'button_id',   name: 'button_name' },
};

/**
 * Dropdown values carry their goal type, because the radio group that used to
 * supply it is gone: "webhook::<encrypted id>", or "button::__all__".
 * Encrypted ids are base64 (A–Z a–z 0–9 + / =) so they can never contain "::".
 * The id half is passed to the backend untouched, which is what keeps saved
 * tests and existing report URLs working.
 */
const SEP = '::';

export function composeGoalValue(goalType: string, value: string): string {
  return `${goalType}${SEP}${value}`;
}

export function parseGoalValue(composite: any): { type: string; value: string } | null {
  if (typeof composite !== 'string' || composite === '') return null;
  const at = composite.indexOf(SEP);
  if (at < 0) return null;
  const type = composite.slice(0, at);
  const value = composite.slice(at + SEP.length);
  if (!(GOAL_TYPES as readonly string[]).includes(type) || value === '') return null;
  return { type, value };
}

type SelectItem = { value: string; label: string; disabled?: boolean };

/**
 * "All Conversions" is the pre-existing roll-up option, not a goal in its own
 * right, so it never counts toward whether an "All ..." option is offered.
 */
const isAllConversions = (label: any) =>
  typeof label === 'string' && label.trim().toLowerCase() === 'all conversions';

/** Real goals in a dropdown list (excludes the All Conversions roll-up). */
export function countRealGoals(options: any, idKey: string, nameKey: string): number {
  const rows = Array.isArray(options) ? options : Object.values(options || {});
  return rows.filter((row: any) => row && row[idKey] && !isAllConversions(row[nameKey])).length;
}

/**
 * Dropdown items for one goal type: the goals themselves, then "All ..." last.
 * "All" appears only when the type has TWO OR MORE goals - a single goal shows
 * just that goal, and no goals shows nothing.
 *
 * Availability is decided from the option list itself, so the dropdown is correct
 * even if the backend's all_goals_available flag is absent (older build, cached
 * response). When the flag IS supplied it is honoured as authoritative.
 *
 * @param options     raw list from the conversions-dropdown API
 * @param idKey       e.g. 'button_id'
 * @param nameKey     e.g. 'button_name'
 * @param goalType    goal|button|postback|webhook
 * @param canShowAll  all_goals_available[goalType] from the API, or undefined
 */
type GroupedItem = { value: string; label: string; group: string };

/**
 * The single searchable dropdown that replaced the radio group.
 *
 * One flat list of Mantine items carrying a `group`, rendered in a fixed order:
 * Page Goals, Webhook Goals, Postback Goals, Button Goals. A category with no
 * goals contributes nothing, so empty headings never appear. Each category ends
 * with its "All ..." entry, but only where that type has two or more real goals.
 *
 * Values are type-prefixed (see composeGoalValue) because the goal type no longer
 * comes from a radio. The id half is unchanged, so everything the backend
 * receives — and every saved test — keeps working.
 *
 * @param lists      { goal, webhook, postback, button } as returned by the API
 * @param available  all_goals_available from the API, or {} to infer from counts
 */
export function buildGroupedGoalData(
  lists: Record<string, any>,
  available: Record<string, boolean | undefined> = {},
  allOptionValue: string = ALL_OPTION_VALUE,
): GroupedItem[] {
  const out: GroupedItem[] = [];

  GOAL_GROUP_ORDER.forEach((type) => {
    const keys = GOAL_LIST_KEYS[type];
    const raw = lists ? lists[type] : null;
    const rows = Array.isArray(raw) ? raw : Object.values(raw || {});
    const usable = rows.filter((row: any) => row && row[keys.id]);
    if (!usable.length) return;                       // hide the empty category

    const group = GOAL_GROUP_LABEL[type];
    const flag = available ? available[type] : undefined;
    /* Page Goals never gets an "All ..." entry. Its roll-up is the original
       "All Conversions" option, which arrives as a normal goal in the list. */
    const showAll = type === 'goal'
      ? false
      : (flag === undefined ? countRealGoals(usable, keys.id, keys.name) >= 2 : !!flag);

    usable.forEach((row: any) => {
      out.push({ value: composeGoalValue(type, row[keys.id]), label: row[keys.name] ?? '-', group });
    });
    /* "All ..." closes its category, after the individual goals - matching Page
       Goals, where the pre-existing "All Conversions" roll-up is already last. */
    if (showAll) {
      out.push({ value: composeGoalValue(type, allOptionValue), label: allGoalLabel(type), group });
    }
  });

  return out;
}

export function buildGoalSelectData(
  options: any,
  idKey: string,
  nameKey: string,
  goalType: string,
  canShowAll?: boolean,
  allOptionValue: string = ALL_OPTION_VALUE,
): SelectItem[] {
  const list: SelectItem[] = [];
  const showAll = canShowAll === undefined
    ? countRealGoals(options, idKey, nameKey) >= 2
    : !!canShowAll;
  const rows = Array.isArray(options) ? options : Object.values(options || {});
  rows.forEach((row: any) => {
    if (!row || !row[idKey]) return;
    list.push({ value: row[idKey], label: row[nameKey] ?? '-' });
  });
  /* "All ..." last, after the individual goals. */
  if (showAll) {
    list.push({ value: allOptionValue, label: allGoalLabel(goalType) });
  }
  return list;
}
